import { useTheme } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import useEmail from "hooks/use-email.hook";
import { useGetAmbientScribeQuery } from "services/ambient-scribe";
import { useGetClinicQuery } from "services/clinics";
import { useGetPatientQuery } from "services/patients";
import { Patient } from "services/types/patients.type";

/** Minimal read: the id keys the cache, `clinic` keys the email subject. */
const AVS_EMAIL_SCRIBE_QUERY = "{id,status,clinic{id,name}}";

/**
 * Everything the "Email to Patient" action needs, shared by the After Visit Summary dialog and the
 * scribe-details tab so the two surfaces cannot drift on the subject rule.
 *
 * The approved subject rule (Intercom 215475744511616): "Your After Visit Summary from {clinic}",
 * where {clinic} is the clinic where the visit happened by DISPLAY name, falling back to the
 * practice name. `Clinic.display_name` from `/clinics/{id}/` already applies that fallback
 * server-side (`display_name_prop`); the practice name here covers a recording with no clinic.
 */
export default function useAfterVisitSummaryEmail({
  scribeId,
  patientId,
  enabled
}: {
  scribeId: number;
  patientId: number | undefined;
  enabled: boolean;
}): {
  patient: Patient | undefined;
  emailSubject: string;
  /** True once the patient read has actually returned a patient. See the note at the query. */
  isPatientLoaded: boolean;
  /**
   * True when the letter can actually be emailed. False when the patient has no address on file:
   * `getDefaultValueFromPatient` yields an empty `recipients` in that case and the To field is
   * `required`, so a caller that only checks `patient` hands the user a blank required field with
   * no explanation. Also false while the patient is still loading.
   */
  canEmail: boolean;
  /** Why `canEmail` is false, for the button tooltip. Empty string when it is true. */
  cannotEmailReason: string;
  /**
   * Opens the shared email form prefilled with the letter. No-op without a patient, a patient email,
   * or a letter. Pass `fromScribeModal` when opening from inside the scribe details modal so the
   * form paints above it rather than underneath.
   */
  openEmail: (
    letterHtml: string,
    options?: { fromScribeModal?: boolean; noteId?: number }
  ) => boolean;
} {
  const { handleOpen: openEmailForm } = useEmail();
  const theme = useTheme();
  const currentPractice = useAppSelector(selectCurrentPractice);

  // The `query` projection is NOT optional in practice. `getPatient` defaults it to `"{}"`, and
  // RestQL reads an empty inclusion block as "select nothing" — `GET /patients/{id}/?query={}`
  // returns a bare `{}`. `formatPatient` then makes that TRUTHY (it fills `fullName: ""`, an
  // avatar fallback and a zero balance), so the patient reads as loaded-with-no-email: both Send
  // buttons disabled and `cannotEmailReason` claiming there is no address on file. Every other
  // `useGetPatientQuery` caller in the repo passes a projection; this one must too.
  //
  // `PATIENT_HEADER_QUERY` rather than a narrower AVS constant: it is what the other patient
  // dialogs (`EmailMenu`, `FaxMenu`, the recording widget) send, so on the patient page this
  // often lands in a cache entry that already exists, and it carries both what the email form
  // needs (`email`, name parts, `preferred_clinic{id}` for sender derivation) and what the print
  // header needs (`suffix`, `birthdate`, `sex`, `age_years`, `phone`, `physicians`).
  const { data: patient } = useGetPatientQuery(
    { patientId: patientId as number, query: PATIENT_HEADER_QUERY },
    { skip: !enabled || patientId == null }
  );
  const { data: scribe } = useGetAmbientScribeQuery(
    { id: scribeId, query: AVS_EMAIL_SCRIBE_QUERY },
    { skip: !enabled }
  );
  // `AmbientScribe.clinic` is typed as a bare id, but with a nested `?query=` projection the
  // server returns an object — read both shapes.
  const rawClinic = scribe?.clinic as
    | { id?: number }
    | number
    | string
    | null
    | undefined;
  const clinicId =
    typeof rawClinic === "object" && rawClinic
      ? rawClinic.id
      : rawClinic != null
        ? Number(rawClinic)
        : undefined;
  const { data: clinic } = useGetClinicQuery(
    { clinicId: clinicId as number },
    { skip: !enabled || clinicId == null }
  );

  const clinicName = clinic?.display_name || currentPractice?.name || "";
  const emailSubject = clinicName
    ? `Your After Visit Summary from ${clinicName}`
    : "Your After Visit Summary";

  // Keyed on `id`, not on `patient` being truthy. `formatPatient` returns a populated-looking
  // object for an empty response, so truthiness does not mean "loaded" — which is exactly how the
  // projection bug above came to show a confident, wrong "no email address on file".
  const isPatientLoaded = Boolean(patient?.id);
  const canEmail = isPatientLoaded && Boolean(patient?.email);
  const cannotEmailReason =
    isPatientLoaded && !patient?.email
      ? "This patient has no email address on file"
      : "";

  const openEmail = (
    letterHtml: string,
    {
      fromScribeModal = false,
      noteId
    }: { fromScribeModal?: boolean; noteId?: number } = {}
  ): boolean => {
    if (!canEmail || !patient || !letterHtml) return false;
    openEmailForm({
      patient,
      emailContent: letterHtml,
      emailSubject,
      // Recording the send server-side is why the "Emailed" state survives a reload and why the
      // preview shows the body as sent rather than as generated. Passing it from here (behind the
      // `afterVisitSummary` flag with the rest of these surfaces) is also what keeps the new write
      // key off every other caller's request.
      ...(noteId != null && { ambientScribeNoteId: noteId }),
      // The scribe details modal raises itself to `getScribeModalZIndex` (1501); the email form
      // defaults to MUI's 1300 and would render behind it. `getScribeModalNestedDialogZIndex` is the
      // constant every other nested dialog in that modal already uses.
      ...(fromScribeModal && {
        zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar)
      })
    });
    return true;
  };

  return {
    patient,
    isPatientLoaded,
    emailSubject,
    canEmail,
    cannotEmailReason,
    openEmail
  };
}
